import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing context menu state
 * @returns {object} Context menu state and operations
 */
export const useContextMenu = () => {
  const [menuState, setMenuState] = useState({
    open: false,
    position: null,
    item: null,
  });

  // Show context menu at position
  const showMenu = useCallback((event, item) => {
    event.preventDefault();
    event.stopPropagation();
    
    setMenuState({
      open: true,
      position: { x: event.clientX, y: event.clientY },
      item,
    });
  }, []);

  // Hide context menu
  const hideMenu = useCallback(() => {
    setMenuState({
      open: false,
      position: null,
      item: null,
    });
  }, []);

  // Close menu on click outside or Escape
  useEffect(() => {
    if (!menuState.open) return;

    const handleClick = () => hideMenu();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') hideMenu();
    };
    const handleScroll = () => hideMenu();

    // Delay adding click listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      window.addEventListener('click', handleClick);
    }, 0);
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuState.open, hideMenu]);

  return {
    open: menuState.open,
    position: menuState.position,
    item: menuState.item,
    showMenu,
    hideMenu,
  };
};

export default useContextMenu;
